import { useState, useRef, useCallback, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, MessageSquarePlus } from "lucide-react";
import { Button } from "../ui/button";
import Review360Viewer from "./Review360Viewer";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

function fmtTime(seconds) {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Renders the media for a review asset (image / video / pdf) with pin/marker
// interaction. Shared between the internal staff workspace and the public
// client review page so both get identical annotation behavior.
export default function ReviewAssetViewer({
  asset,
  fileUrl,
  comments,
  activeCommentId,
  onPinDraft,
  onRequestTimeComment,
  onRequestPageComment,
  onSelectComment,
  seekToTime,
  jumpToPage,
  goToYawPitch,
}) {
  const imgRef = useRef(null);
  const videoRef = useRef(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setPageNumber(1);
    setCurrentTime(0);
  }, [asset?.id]);

  useEffect(() => {
    if (seekToTime != null && videoRef.current) {
      videoRef.current.currentTime = seekToTime;
      videoRef.current.play().catch(() => {});
    }
  }, [seekToTime]);

  useEffect(() => {
    if (jumpToPage != null) setPageNumber(jumpToPage);
  }, [jumpToPage]);

  const handleImageClick = useCallback(
    (e) => {
      if (!imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      onPinDraft?.({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    },
    [onPinDraft]
  );

  if (!asset) return null;

  if (asset.file_type === "image" && asset.is_360) {
    return (
      <Review360Viewer
        asset={asset}
        fileUrl={fileUrl}
        comments={comments}
        activeCommentId={activeCommentId}
        onPinDraft={(pin) => onPinDraft?.({ yaw: pin.yaw, pitch: pin.pitch })}
        onSelectComment={onSelectComment}
        goToYawPitch={goToYawPitch}
      />
    );
  }

  if (asset.file_type === "image") {
    const pins = comments.filter((c) => c.pin_x != null && c.pin_y != null && !c.parent_comment_id);
    return (
      <div className="relative inline-block max-w-full select-none" style={{ cursor: "crosshair" }}>
        <img
          ref={imgRef}
          src={fileUrl}
          alt={asset.original_name}
          onClick={handleImageClick}
          className="max-w-full max-h-[70vh] rounded-lg border border-border block"
          draggable={false}
        />
        {pins.map((c, i) => (
          <button
            key={c.id}
            onClick={(e) => {
              e.stopPropagation();
              onSelectComment?.(c.id);
            }}
            title={c.body}
            className={`absolute flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold text-white shadow-md border-2 border-white -translate-x-1/2 -translate-y-1/2 transition-transform ${
              activeCommentId === c.id ? "scale-125 bg-blue-600 z-10" : "bg-red-500 hover:scale-110"
            }`}
            style={{ left: `${c.pin_x}%`, top: `${c.pin_y}%` }}
          >
            {i + 1}
          </button>
        ))}
      </div>
    );
  }

  if (asset.file_type === "video") {
    const markers = comments.filter((c) => c.timestamp_seconds != null && !c.parent_comment_id);
    return (
      <div className="max-w-full">
        <video
          ref={videoRef}
          src={fileUrl}
          controls
          className="max-w-full max-h-[65vh] rounded-lg border border-border bg-black block"
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        />
        {duration > 0 && (
          <div className="relative h-6 mt-2 bg-muted rounded-full">
            {markers.map((c) => (
              <button
                key={c.id}
                title={`${fmtTime(c.timestamp_seconds)} — ${c.body}`}
                onClick={() => {
                  onSelectComment?.(c.id);
                  if (videoRef.current) videoRef.current.currentTime = c.timestamp_seconds;
                }}
                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-white shadow ${
                  activeCommentId === c.id ? "bg-blue-600 scale-125" : "bg-red-500"
                }`}
                style={{ left: `${(c.timestamp_seconds / duration) * 100}%` }}
              />
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">{fmtTime(currentTime)} / {fmtTime(duration)}</span>
          <Button size="sm" variant="outline" onClick={() => onRequestTimeComment?.(currentTime)}>
            <MessageSquarePlus className="w-3.5 h-3.5 mr-1.5" />
            Comment at {fmtTime(currentTime)}
          </Button>
        </div>
      </div>
    );
  }

  if (asset.file_type === "pdf") {
    return (
      <div className="max-w-full flex flex-col items-center">
        <Document file={fileUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={<p className="text-sm text-muted-foreground py-8">Loading PDF…</p>}>
          <Page pageNumber={pageNumber} width={720} renderAnnotationLayer={false} renderTextLayer={false} />
        </Document>
        <div className="flex items-center gap-3 mt-3">
          <Button size="icon" variant="outline" disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {pageNumber} of {numPages || "…"}</span>
          <Button size="icon" variant="outline" disabled={pageNumber >= numPages} onClick={() => setPageNumber((p) => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onRequestPageComment?.(pageNumber)}>
            <MessageSquarePlus className="w-3.5 h-3.5 mr-1.5" />
            Comment on this page
          </Button>
        </div>
      </div>
    );
  }

  return <p className="text-sm text-muted-foreground">Unsupported file type</p>;
}

export { fmtTime };
